import { Component, OnInit, Injector, ApplicationRef, Inject } from '@angular/core';
import { ComponentFactoryResolver, ComponentRef } from '@angular/core';
import { IncidenciaComponent } from '../markers/incidencia/incidencia.component';
/// <reference types="@types/googlemaps" />
import { default as MarkerClusterer } from '@google/markerclustererplus';
import { DadesService } from '../../services/dades.service';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { ZoomDialogComponent } from '../zoom-dialog/zoom-dialog.component';
import { CheckboxesService } from '../../services/checkboxes.service';
import { InrixService } from '../../services/inrix.service';
import { MercatorProjection } from '../../mercator-projection';
import { Observable, interval, Subscription, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CameraComponent } from '../markers/camera/camera.component';
import { RadarComponent } from '../markers/radar/radar.component';
import { ParkingComponent } from '../markers/parking/parking.component';
import { ParkNRideComponent } from '../markers/park-nride/park-nride.component';
import { GasStationComponent } from '../markers/gas-station/gas-station.component';
import { TramPerillComponent } from '../markers/tram-perill/tram-perill.component';
import { DirectionsService } from '../../services/directions.service';
import { STYLE } from '../../../styles';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { http_build_query } from 'locutus/php/url';
import { DomSanitizer } from '@angular/platform-browser';
import * as jQuery from 'jquery';

/**
 * Component principal de l'aplicació. S'encarrega de fer tots els canvis necessaris al mapa per mostrar la informació que l'usuari ha escollit
 * fent servir els altres components de l'aplicació.
 */
@Component({
  selector: "app-map-container",
  templateUrl: "./map-container.component.html",
  styleUrls: ["./map-container.component.css"]
})
export class MapContainerComponent implements OnInit {
  /**
   * Constructor del component
   * @param {DadesService} dadesService Servei que connecta amb l'API Infotransit
   * @param {CheckboxesService} checkboxesService Servei que conté els noms de les capes de marcadors activades
   * @param {DirectionsService} directionsService Servei de càlcul de rutes de l'aplicació
   * @param {MatDialog} dialog Obre la finestra per preguntar si es vol fer zoom
   * @param {InrixService} inrixService Servei que connecta amb Inrix
   * @param {ComponentFactoryResolver} resolver Carrega components dinàmicament per injectar-los a la finestra d'informació de l'aplicació 
   * @param {Injector} injector Injecta informació als components carregats dinàmicament
   * @param {ApplicationRef} appRef Manté una referència a elements de l'aplicació
   * @param {ActivatedRoute} route Indica la ruta URL actual de l'aplicació
   */
  constructor(private dadesService: DadesService, private checkboxesService: CheckboxesService, private directionsService: DirectionsService,
    public dialog: MatDialog, private inrixService: InrixService, private resolver: ComponentFactoryResolver,
    private injector: Injector, private appRef: ApplicationRef, private route: ActivatedRoute, private sanitizer: DomSanitizer) { }

  /**
   * Referència a component
   */
  compRef: ComponentRef<any>;

  /**
   * Objecte únic. Tot treballa entorn aquest instància del mapa
   */
  map: google.maps.Map;

  /**
   * Bounds del mapa extesos. Si no sortim d'aquests bounds, no es carreguen noves dades de les capes amb zoom (ja estan carregades)
   */
  marginBounds: google.maps.LatLngBounds; // 

  /**
   * Variable pel càlcul de bounds extesos. Indica el límit sud-oest
   */
  SWactual;

  /**
   * Variable pel càlcul de bounds extesos. Indica el límit nord-est
   */
  NEactual;

  /**
   * Ens podem subscriure per executar algunes funcions cada cert temps
   */
  updateSubscription: Subscription;

  /**
   * Subject per emetre events
   */
  updateSubject: Subject<void> = new Subject<void>();

  /**
   * Centre del mapa
   */
  center = new google.maps.LatLng(40.38937, -3.511634);

  /**
   * Gestiona el clustering del mapa
   */
  markerCluster: MarkerClusterer = new MarkerClusterer(this.map);

  /**
   *  Per cada capa que afegim, es crea una nova parella key=nomCapa i value=array de marcadors d'aquesta capa
   */
  loadedLayers: Map<string, Map<string, google.maps.Marker>> = new Map();

  /**
   * Nom de les capes del tipus alerta
   */
  alertes = ["ROADWORKS", "CONGESTION", "INCIDENT", "MOUNTAINPASS", "METEO", "CONES"];

  /**
   * Nom de les capes del tipus servei
   */
  serveis = ["GAS_STATION", "RADAR", "PARKING", "PARKANDRIDE", "TRAMS_PERILL"];

  /**
   * Nom de les capes que necessiten un zoom mínim per ser mostrades
   */
  zoomLayers = ["CAMERA", "RADAR", "GAS_STATION", "PARKING", "PARKANDRIDE"];

  /**
   * Indica les capes que estan activades en cada moment
   */
  activeLayers = [];

  /**
   * Marcadors de les capes d'incidències que són visibles als bounds del mapa actual
   */
  alertesVisibles = new Map();

  /**
   * Conté les features d'incidències carregades al mapa
   */
  alertesFeatures = [];

  /**
   * Conté les features d'incidències carregades al mapa i a dins dels bounds actuals
   */
  alertesVisiblesFeatures = [];

  /**
   * Identificador de la causa d'incidència meteorològica
   */
  meteoId: string;

  /**
   * Per cada identificador de causa indica el seu nom en castellà
   */
  meteoIds: Map<string, string> = new Map([["20", "Niebla"], ["28", "Cadenas"], ["21", "Hielo"], ["42", "Inundaciones"], ["26", "Lluvia"], ["27", "Nieve"], ["33", "Viento"]]);

  /**
   * Finestra d'informació del mapa. Només hi ha una que canvia el seu contingut en funció del marcador clicat
   */
  infowindow = new google.maps.InfoWindow();

  /**
   * Capa de tràfic INRIX
   */
  trafficLayer: google.maps.ImageMapType;

  /**
   * Determina si el tràfic està en mode directe o mode previsió
   */
  trafficMode: string;

  /**
   * En format ISO, indica la hora per la qual es fa la previsió de trànsit amb INRIX
   */
  trafficTime: string;
  /**
   * Tipus de mapa RACC
   */
  raccMapType = new google.maps.StyledMapType(STYLE, { name: 'racc' });
  isInrixVisible = false;
  /**
   * Opcions del mapa
   */
  mapProperties: google.maps.MapOptions = {
    zoom: 7,
    center: this.center,
    disableDefaultUI: false,
    styles: STYLE,
    gestureHandling: 'greedy',
    mapTypeControlOptions: {
      mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain']
    },
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false

  };

  /**
   * Retorna els bounds passats per paràmetre segons el format de la base de dades
   * @param {google.maps.LatLngBounds} bounds Límits del mapa
   * @returns {string} El format que es retorna és un String amb "<nordOest.longitud>,<nordOest.latitud>,<sudEst.longitud>,<sudEst.latitud>"
   */
  formatBounds(bounds: google.maps.LatLngBounds): string {
    var nord_est = bounds.getNorthEast();
    var sud_oest = bounds.getSouthWest();
    var nord_oest = sud_oest.lng() + "," + nord_est.lat();
    var sud_est = nord_est.lng() + "," + sud_oest.lat();
    return nord_oest + "," + sud_est;
  }

  /**
   * Donats els bounds del mapa actuals, genera uns bounds amb els límits extesos segons el paràmetre percentatge
   * @param percentatge percentatge pel qual es vol ampliar els bounds del mapa. Valor decimal
   * @return {google.maps.LatLngBounds} Límits extesos del mapa
   */
  extendBounds(percentatge: number): google.maps.LatLngBounds {
    var x1, x2, y1, y2, x1n, x2n, y1n, y2n;
    var bounds = this.map.getBounds();

    x1 = bounds.getSouthWest().lng();
    y1 = bounds.getSouthWest().lat();

    x2 = bounds.getNorthEast().lng();
    y2 = bounds.getNorthEast().lat();

    x1n = x1 - (x2 - x1) * percentatge;
    y1n = y1 - (y2 - y1) * percentatge;

    x2n = x2 + (x2 - x1) * percentatge;
    y2n = y2 + (y2 - y1) * percentatge;

    var extendedSW = new google.maps.LatLng(y1n, x1n);
    var extendedNE = new google.maps.LatLng(y2n, x2n);

    var newBounds = new google.maps.LatLngBounds(
      extendedSW,
      extendedNE
    );

    return newBounds;
  }

  /**
   * Verifica que el mapa s'ha mogut el suficient com per tornar a fer una crida a base de dades per obtenir nous marcadors
   * @param percentatge percentatge de quantitat de pantalla que s'ha d'haver mogut per fer una nova crida
   * @returns {boolean} Indica si s'han de refrescar els límits extesos o no
   */
  checkBoundsRefresh(): boolean {

    var bounds = this.map.getBounds();
    var SWactual = bounds.getSouthWest();
    var NEactual = bounds.getNorthEast();

    if (
      SWactual.lat() < this.marginBounds.getSouthWest().lat() || SWactual.lng() < this.marginBounds.getSouthWest().lng() ||
      NEactual.lat() > this.marginBounds.getNorthEast().lat() || NEactual.lng() > this.marginBounds.getNorthEast().lng()
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Activa o desactiva la capa de tràfic del mapa
   * @param {string} event text que identifica l'event
   */
  toggleTraffic(event: string): void {

    if (event === 'toggle') {

      this.isInrixVisible = !this.isInrixVisible;
    }
    else if (event.startsWith('Trànsit en previsió')) {
      this.trafficMode = 'Trànsit en previsió';
      var time = event.split('|')[1];
      this.trafficTime = time;
    }
    else if (event.startsWith('Trànsit en directe')) {
      this.trafficMode = 'Trànsit en directe';
      this.trafficTime = "";

    } else {
      this.trafficTime = event;
    }

    this.changeInrixTraffic();

  }

  /**
   * Verifica si la capa que es vol carregar és una de les que necessita un zoom mínim per mostrar-se
   * @param {string} layer nom de la capa
   * @returns {boolean} Retorna si s'ha de fer zoom o no per veure la capa que s'acaba d'activar
   */
  needsZoom(layer: string): boolean {
    return this.zoomLayers.includes(layer) && this.map.getZoom() < 10;
  }

  /**
   * Si el mapa no té el zoom mínim, elimina les capes que necessiten aquest zoom mínim
   */
  checkZoomLayers(): void {
    if (this.map.getZoom() < 10) {
      this.zoomLayers.forEach(layer => {
        if (this.loadedLayers.has(layer)) {
          this.removeLayer(layer);
          this.checkboxesService.deleteCheckbox(layer);
        }
      });
    }
  }

  /**
   * Carrega o esborra les capes del mapa segons si estan o no activades
   * @param {string[]} activeLayers llista amb capes activades per l'usuari actualment
   */
  updateActiveLayers(activeLayers: string[]): void {
    //Esborrem del mapa les layers que han desactivat
    this.activeLayers.forEach(layer => {
      if (!activeLayers.includes(layer)) {
        this.toggleLayer(layer);
        var index = this.activeLayers.indexOf(layer);
        this.activeLayers.splice(index, 1);
      }
    })
    //Afegim al mapa les layers que s'han activat
    activeLayers.forEach(layer => {
      if (!this.activeLayers.includes(layer)) {
        if (this.toggleLayer(layer)) this.activeLayers.push(layer);
      }
    })
  }

  /**
   * Carrega o esborra els marcadors de la capa "layer". Retorna cert  si s'ha afegit o tret alguna capa. Fals altrament
   * @param {string} layer nom de la capa
   */
  toggleLayer(layer: string): boolean {
    var ret = true;
    if (this.loadedLayers.has(layer)) {
      this.removeLayer(layer);
    } else {
      if (this.needsZoom(layer) && !this.activeLayers.includes(layer)) {
        this.openAcceptZoomDialog(layer);
      }
      else if (!this.needsZoom(layer)) {
        this.addLayer(layer);
        ret = true;
      }
    }
    this.infowindow.close();
    return ret;
  }

  /**
   * Afegeix una capa de marcadors al mapa carregant un geojson per HTTP GET.
   * Es crea una nova entrada al diccionari "loadedLayers" d'aquest objecte amb key=<layer> i value=<diccionari de marcadors que hem carregat>
   * @param {string} layer nom de la capa que volem afegir
   */
  addLayer(layer: string) {
    var geojson;
    if (!this.marginBounds) this.marginBounds = new google.maps.LatLngBounds();
    else this.marginBounds = this.extendBounds(0.8);
    this.loadedLayers.set(layer, new Map());
    if (layer === 'PARKING') this.addLayer('PARKANDRIDE');
    this.dadesService
      .getLayer(layer, this.formatBounds(this.marginBounds))
      .subscribe(data => {
        geojson = data;
        geojson.features.forEach(feature => {
          this.addMarker(feature, layer);
          feature.properties.layer = layer;
          this.alertesFeatures.push(feature);
        });
        this.loadedLayers.get(layer).forEach((value) => {
          this.markerCluster.addMarker(value);

        })
        this.updateVisibleMarkers();
      });
  }

  /**
   * Treu del mapa els marcadors corresponents a una capa. També s'esborra l'entrada al diccionari
   * @param {string} layer nom de la capa que volem esborrar
   */
  removeLayer(layer: string) {
    this.loadedLayers.get(layer).forEach((value, key) => {
      value.setMap(null);
      var index = this.alertesFeatures.findIndex(alerta => alerta.properties.id === key);
      this.alertesFeatures.splice(index, 1);
    });
    this.markerCluster.removeMarkers(Array.from(this.loadedLayers.get(layer).values()));
    this.loadedLayers.delete(layer);
    if (layer === "PARKING") this.removeLayer('PARKANDRIDE');
    this.updateVisibleMarkers();
  }

  /**
   * Torna a carregar totes les capes actives al mapa
   */
  refreshActiveLayers() {
    this.activeLayers.forEach(layer => {
      this.removeLayer(layer);
      this.addLayer(layer);
    })
  }

  /**
   * Actualitza el contingut de l'array que conté els marcadors visibles dins els bounds actual de les capes d'alertes
   */
  updateVisibleMarkers(): void {
    let bounds = this.map.getBounds(); // Límits del mapa
    var markersIncidenciesVisibles = new Map<string, google.maps.Marker>();


    // Tots els marcadors visibles al mapa
    this.loadedLayers.forEach((layerValue, layerKey) => {
      if (this.alertes.includes(layerKey)) {
        layerValue.forEach((markerValue, markerKey) => {
          if (bounds.contains(markerValue.getPosition())) markersIncidenciesVisibles.set(markerKey, markerValue);
        })
      }
    });

    //Esborrem de alertesVisibles aquelles que ja no són visibles
    this.alertesVisibles.forEach((alertaValue, alertaKey) => {
      if (!markersIncidenciesVisibles.has(alertaKey)) this.alertesVisibles.delete(alertaKey);
    });

    //Afegim a alertesVisibles els marcadors visibles que abans no hi eren
    markersIncidenciesVisibles.forEach((markerValue, markerKey) => {
      this.alertesVisibles.set(markerKey, markerValue);
    })


    this.alertesVisiblesFeatures = this.alertesFeatures.filter(alerta => this.alertesVisibles.has(alerta.properties.id));
  }


  /**
   * Emet un event per l'updateSubject que pot ser escoltat pels components fills
   */
  emitRefreshToChild() {
    this.updateSubject.next()
  }

  /**
   * Actualitza els marcadors de la capa amb identificador `layer` que necessiten un zoom mínim per mostrar-se
   * @param {string} layer nom de la capa que es vol actualitzar
   */
  updateZoomLayer(layer: string) {
    if (this.loadedLayers.has(layer)) {
      var geojson;
      this.dadesService
        .getLayer(layer, this.formatBounds(this.marginBounds))
        .subscribe(data => {
          geojson = data;
          geojson.features.forEach(feature => {
            var marker;
            if (!this.loadedLayers.get(layer).has(feature.properties.id)) {
              marker = this.addMarker(feature, layer);
              this.markerCluster.addMarker(marker);
            }
          })
        })

    }
  }

  /**
   * Afegeix un marcador al mapa tenint en compte les propiertats del feature i la capa a la qual pertany
   * @param {json} feature conté la informació i la localització del nou marcador a afegir
   * @param {json} layer indica la capa a la qual pertany aquest marcador
   */
  addMarker(feature, layer) {
    let marker = new google.maps.Marker({
      position: {
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0]
      },
      icon: {
        url: this.getIconUrl(feature, layer),
        scaledSize: this.getIconSize(feature, layer)
      },
      map: this.map
    });
    marker.addListener("click", () => {
      this.infowindow.setContent(this.loadInfoWindow(feature, layer));
      this.infowindow.open(this.map, marker);
    });
    this.loadedLayers.get(layer).set(feature.properties.id, marker);
    return marker;
  }

  /**
   * Donat una feature que pertany a un layer, retorna la url de la imatge que correspon a la icona d'aquest marcador
   * @param {json} feature conté la informació i la localització del nou marcador a afegir
   * @param {json} layer indica la capa a la qual pertany aquest marcador
   */
  getIconUrl(feature, layer) {
    var url: string;
    if (layer === "METEO") {
      var nomCausa = this.meteoIds.get(feature.properties.causeId);
      url = 'assets/img/' + nomCausa;
      if (feature.properties.levelOfService === '5') url = url + 'Negro';
    }
    else if (layer === "TRAMS_PERILL") {
      url = 'assets/img/eurorap' + feature.properties.levelOfService;
    }
    else url = 'assets/img/' + layer + '_MARKER';

    return url + '.svg';
  }

  /**
   * Retorna la mida de la icona del marcador depenent del tipus d'aquest
   * @param {json} feature conté la informació i la localització del nou marcador a afegir
   * @param {json} layer indica la capa a la qual pertany aquest marcador
   */
  getIconSize(feature, layer) {
    var size = new google.maps.Size(53.29, 61);
    if (layer === "METEO" && feature.properties.levelOfService === '5') size = new google.maps.Size(58.62, 67.1);
    return size;
  }

  /**
   * Genera i retorna el contingut d'un component que conté la informació a mostrar a la infowindow
   * @param {string} feature Conté les dades del marcador al qual li volem afegir la finestra d'informació
   * @param {string} layer Identificador de la capa a la qual pertany el feature 
   * @returns {HTMLDivElement} HTML amb informació a mostrar a la finestra
   */
  loadInfoWindow(feature, layer): HTMLDivElement {
    if (this.compRef) this.compRef.destroy();
    var compFactory;
    if (this.alertes.includes(layer)) compFactory = this.resolver.resolveComponentFactory(IncidenciaComponent);
    else if (layer === "CAMERA") compFactory = this.resolver.resolveComponentFactory(CameraComponent);
    else if (layer === "RADAR") compFactory = this.resolver.resolveComponentFactory(RadarComponent);
    else if (layer === "PARKING") compFactory = this.resolver.resolveComponentFactory(ParkingComponent);
    else if (layer === "PARKANDRIDE") compFactory = this.resolver.resolveComponentFactory(ParkNRideComponent);
    else if (layer === "GAS_STATION") compFactory = this.resolver.resolveComponentFactory(GasStationComponent);
    else if (layer === "TRAMS_PERILL") compFactory = this.resolver.resolveComponentFactory(TramPerillComponent);
    this.compRef = compFactory.create(this.injector);
    this.compRef.instance.feature = feature;
    this.compRef.instance.layer = layer;
    this.appRef.attachView(this.compRef.hostView);
    let div = document.createElement('div');
    div.appendChild(this.compRef.location.nativeElement);
    return div;
  }

  /**
   * Centra el mapa en la ubicació buscada
   * @param {google.maps.places.PlaceGeometry} place ubicació que s'ha cercat per l'usuari 
   */
  searchPlace(place: google.maps.places.PlaceGeometry) {
    this.map.fitBounds(place.viewport);
  }


  /**
   * Obre un dialog per preguntar a l'usuari si accepta fer zoom per carregar la capa layer. Retorna cert si s'accepta, fals altrament
   * @param {string} layer capa que es vol carregar que necessita un zoom mínim
   */
  openAcceptZoomDialog(layer: string) {
    var ret = false;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;

    dialogConfig.data = {
      acceptZoom: false
    };

    const dialogRef = this.dialog.open(ZoomDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        //Zoom acceptat
        ret = true;
        this.map.setZoom(10);
        this.addLayer(layer);
        return ret = true;
      }
      else {
        ret = false
        this.checkboxesService.deleteCheckbox(layer);
        return ret;
      }
    });

  }



  /***********************
   *                     *
   *  FUNCIONS INRIX     *
   *                     * 
   ***********************/

  /**
   * Inicialitza la capa de tràfic cridant l'API de INRIX
   */
  initInrixTraffic() {
    this.inrixService.getInrixService();
  }

  /**
   * Canvia la capa de trànsit del mapa cridant a l'API de INRIX
   */
  changeInrixTraffic() {
    if (this.map.overlayMapTypes.getLength() > 0) {
      this.map.overlayMapTypes.removeAt(0);
    }
    if (this.isInrixVisible) {
      this.inrixService.getInrixService();
    }
  }

  /**
   * @ignore
   */
  onEventDirections(events) {
  }


  /**
   * Llegeix els paràmetres de la URL i carrega el mapa segons la informació d'aquests paràmetres
   */
  readQueryParams(): void {

    if (this.route.snapshot.queryParamMap.has('lat')
      && this.route.snapshot.queryParamMap.has('lng')
      && this.route.snapshot.queryParamMap.has('zoom')
      && this.route.snapshot.queryParamMap.has('layers')) {
      var lat = this.route.snapshot.queryParamMap.get('lat');
      var lng = this.route.snapshot.queryParamMap.get('lng');
      var latLng = new google.maps.LatLng(parseFloat(lat), parseFloat(lng));
      this.map.setCenter(latLng);

      var zoom = this.route.snapshot.queryParamMap.get('zoom');
      this.map.setZoom(parseInt(zoom));

      var layers = this.route.snapshot.queryParamMap.getAll('layers');
      this.checkboxesService.clearCheckboxes();
      layers.forEach(layer => {
        this.checkboxesService.addCheckbox(layer);
      })
    }


  }

  /**
   * Inicialitza el component, inicialitzant el mapa, el MarkerClusterer, la capa de tràfic...
   * Afegeix event listeners i se subscriu a varis serveis
   */
  ngOnInit() {
    var mapElement = document.getElementById("map"); //Element HTML que contindrà el mapa
    this.map = new google.maps.Map(mapElement, this.mapProperties);
    this.map.mapTypes.set('racc', this.raccMapType);
    this.map.setMapTypeId('racc');
    this.directionsService.setMap(this.map);
    this.directionsService.deleteDirections();

    //Inicialització del MarkerCluster
    this.markerCluster = new MarkerClusterer(this.map, [], {
      imagePath: "assets/img/m",
      maxZoom: 18,
      gridSize: 80,
      zoomOnClick: true,
      ignoreHidden: true,

    });


    //Inicialització capa tràfic
    this.initInrixTraffic();

    /**
     * Events del mapa
     */
    this.map.addListener("zoom_changed", () => {
      this.checkZoomLayers();
    });

    this.map.addListener("click", () => {
      this.infowindow.close();
    });

    this.map.addListener("idle", () => {
      if (this.map.getZoom() >= 10 && this.checkBoundsRefresh()) {
        this.marginBounds = this.extendBounds(0.8);
        this.zoomLayers.forEach(layer => this.updateZoomLayer(layer));
      }
      this.updateVisibleMarkers();

    });

    //Afegim un listenerOnce per detectar quan el mapa s'ha carregat per complet la primera vegada
    google.maps.event.addListenerOnce(this.map, 'idle', event => {
      this.readQueryParams();
      this.checkboxesService.getCheckboxes().subscribe(data => {

        this.updateActiveLayers(data);
      })
    });

    this.updateSubscription = interval(90000).subscribe(() => {
      this.refreshActiveLayers();
      if (this.map.overlayMapTypes.getLength() > 0) this.changeInrixTraffic();
      this.emitRefreshToChild();
    });


    this.inrixService.getTokenObservable().subscribe((tokenOBJ) => {

      this.trafficLayer = new google.maps.ImageMapType({
        getTileUrl: (tile, zoom) => {
          return this.getTileURLInrix(tile, zoom);
        },
        tileSize: new google.maps.Size(256, 256),
        minZoom: 0,
        maxZoom: 20,
        name: 'Inrix'
      });

      this.trafficLayer.radius = 1738000;
      this.map.mapTypes.set('Inrix', this.trafficLayer);
      if (this.isInrixVisible) {
        this.map.overlayMapTypes.insertAt(0, this.trafficLayer);
      }

    });


  }


   getTileURLInrix(tile, zoom) {
    var mercatorRange = 256;
    var pixelOrigin = new google.maps.Point(mercatorRange / 2, mercatorRange / 2);
    var pixelsPerLonDegree = mercatorRange / 360;
    var pixelsPerLonRadian = mercatorRange / (2 * Math.PI);
    var projection = new MercatorProjection(pixelOrigin, pixelsPerLonDegree, pixelsPerLonRadian);
    var center = "" + projection.topLeftCenter(zoom).lat() + "|" + projection.topLeftCenter(zoom).lng();
    let date = this.trafficMode === 'Trànsit en previsió' ? this.trafficTime : "";

    var url = this.inrixService.getTileUrl(tile, zoom, center, this.trafficMode, date);
    
    return url;
  }
}
